import ReactECharts from 'echarts-for-react';
import type { WidgetConfig } from '../schemas';
import {
  formatDateLabel,
  pivotSeriesByBreakdown,
  resolveBreakdownKey,
  resolveDimensionKey,
  sortByDimension,
} from '../chartFormat';
import { useFieldCatalog, metricLabel, metricFormat, type FieldCatalog } from '../fieldCatalog';
import type { MetricFormat } from '../formatValue';
import { useChartPalette } from '../ChartPaletteContext';
import { resolveWidgetPalette } from '../chartPalette';
import {
  CHART_COLORS,
  COLOR_PREVIOUS,
  AXIS_X_STYLE,
  TITLE_STYLE,
  buildAreaGradient,
  buildChartGrid,
  buildLegendStyle,
  buildTooltip,
  buildXAxisLabel,
  maskNonPositiveForLog,
  resolveDualAxis,
  type LegendPosition,
} from '../chartStyle';

type Row = Record<string, string | number>;

interface WidgetData {
  current: Row[];
  previous: Row[] | null;
  meta: any;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetData | null;
  loading?: boolean;
  error?: string;
}

// Cadre + padding fournis par WidgetShell (WidgetRenderer) : ici, hauteur seulement.
const SHELL = 'h-full';

/**
 * Options ECharts de la courbe — fonction pure, testée sans DOM.
 */
export function buildLineOption(
  config: WidgetConfig,
  data: WidgetData,
  catalog: FieldCatalog | null,
  palette: string[] = CHART_COLORS,
) {
  const metrics = config.metrics ?? [];

  // Axe X = champ temps/dimension (dimension explicite, sinon champ ajouté par la granularité).
  const dimKey = resolveDimensionKey(data.current, config.dimensions, metrics);
  const breakdownKey = resolveBreakdownKey(dimKey, config.dimensions);
  const currentRows = sortByDimension(data.current, dimKey);
  const previousRows = data.previous ? sortByDimension(data.previous, dimKey) : null;

  // En mode breakdown, `currentRows` contient plusieurs lignes par date (une par valeur
  // de la dimension) : l'axe X doit rester les dates uniques triées, pas `currentRows` tel quel.
  const rawValues =
    breakdownKey && dimKey
      ? Array.from(new Set(data.current.map((row) => String(row[dimKey] ?? '')))).sort()
      : dimKey
        ? currentRows.map((row) => String(row[dimKey] ?? ''))
        : [];
  const categories = dimKey
    ? rawValues.map((v) => formatDateLabel(v, dimKey))
    : currentRows.map((_, i) => String(i));

  const display = (config.display ?? {}) as Record<string, unknown>;
  const legendPosition = (display.legend_position as LegendPosition | undefined) ?? 'bottom';
  const showGrid = (display.show_grid as boolean | undefined) ?? true;
  const showYAxis = (display.show_y_axis as boolean | undefined) ?? true;
  const smooth = (display.smooth as boolean | undefined) ?? true;

  const seriesColors = (config.format?.series_colors as Record<string, string> | undefined) ?? {};
  const singleColor = config.format?.color as string | undefined;

  const yAxisScale = (display.y_axis_scale as 'linear' | 'log' | undefined) ?? 'linear';

  // Axe Y par série (1 = axe droit) : comparer deux métriques d'échelles différentes
  // (ex. clics vs coût) sans écraser la plus petite. Logique factorisée dans chartStyle.
  const { yAxis, axisIndexFor, dual } = resolveDualAxis(
    metrics,
    display.series_y_axis as Record<string, 0 | 1> | undefined,
    (m) => metricFormat(catalog, m),
    showGrid,
    showYAxis,
    yAxisScale,
  );

  const formats: Record<string, MetricFormat> = {};
  const series: object[] = [];

  // Pas de ligne à tracer : option cohérente, sans série (pas de crash côté ECharts).
  const hasRows = currentRows.length > 0;

  metrics.forEach((metric, i) => {
    const label = metricLabel(catalog, metric, config.metric_labels);
    const format = metricFormat(catalog, metric);
    const yAxisIndex = axisIndexFor(metric);

    // Mode breakdown : une courbe par valeur de la dimension (ex. un canal), l'axe X
    // restant le temps. Pas de comparaison période précédente ici (trop de lignes,
    // cf. spec) et pas d'aire sous la courbe (illisible en multi-séries).
    if (breakdownKey && dimKey) {
      const pivot = pivotSeriesByBreakdown(data.current, dimKey, breakdownKey, metric);
      formats[label] = format;
      pivot.series.forEach((s, j) => {
        const seriesName = metrics.length === 1 ? s.key : `${label} · ${s.key}`;
        // Décale le bloc de palette par métrique : sans ça, "Sessions · Direct" et
        // "Coût · Direct" reçoivent la même couleur (j repart à 0 à chaque métrique).
        const color = palette[(i * pivot.series.length + j) % palette.length];
        series.push({
          name: seriesName,
          type: 'line',
          yAxisIndex,
          data: maskNonPositiveForLog(s.data, yAxisScale),
          smooth: smooth ? 0.4 : false,
          symbol: 'circle',
          symbolSize: 5,
          showSymbol: pivot.dates.length <= 40,
          lineStyle: { color, width: 2.5 },
          itemStyle: { color },
          // Échelle log : 0 est invalide (log(0) indéfini), ECharts casse la courbe à
          // chaque valeur nulle. connectNulls la retrace à travers ces points plutôt
          // que de la fragmenter en points isolés (fréquent sur les canaux à faible volume).
          connectNulls: yAxisScale === 'log',
        });
      });
      return;
    }

    const color =
      seriesColors[metric] ??
      (metrics.length === 1 && singleColor ? singleColor : palette[i % palette.length]);

    // Pas de ligne courante à tracer, mais la comparaison période précédente
    // (ci-dessous) reste utile même dans ce cas (ex. 0 session ce mois-ci,
    // du trafic le mois précédent).
    if (hasRows) {
      formats[label] = format;
      series.push({
        name: label,
        type: 'line',
        yAxisIndex,
        data: maskNonPositiveForLog(
          currentRows.map((row) => Number(row[metric]) || 0),
          yAxisScale,
        ),
        smooth: smooth ? 0.4 : false,
        symbol: 'circle',
        symbolSize: 5,
        // Au-delà de ~40 points, les marqueurs saturent la courbe.
        showSymbol: currentRows.length <= 40,
        lineStyle: { color, width: 2.5 },
        itemStyle: { color },
        // Dégradé sous la courbe en série unique seulement (illisible en multi-séries).
        areaStyle: metrics.length === 1 ? buildAreaGradient(color) : undefined,
        connectNulls: yAxisScale === 'log',
      });
    }

    if (previousRows && previousRows.length) {
      const prevLabel = `${label} (préc.)`;
      const prevColor = metrics.length > 1 ? color : COLOR_PREVIOUS;
      formats[prevLabel] = format;
      series.push({
        name: prevLabel,
        type: 'line',
        yAxisIndex,
        data: maskNonPositiveForLog(
          previousRows.map((row) => Number(row[metric]) || 0),
          yAxisScale,
        ),
        smooth: smooth ? 0.4 : false,
        symbol: 'none',
        lineStyle: { color: prevColor, width: 1.5, type: 'dashed', opacity: 0.6 },
        itemStyle: { color: prevColor, opacity: 0.6 },
        connectNulls: yAxisScale === 'log',
      });
    }
  });

  const showLegend = (display.show_legend as boolean | undefined) ?? series.length > 1;
  const hasTitle = !!config.title;
  const xAxisLabel = buildXAxisLabel(categories.length, rawValues);

  return {
    title: hasTitle ? { text: config.title, ...TITLE_STYLE } : undefined,
    tooltip: buildTooltip(formats),
    legend: showLegend ? buildLegendStyle(undefined, legendPosition, hasTitle) : undefined,
    grid: buildChartGrid({
      hasTitle,
      showYAxis,
      dualYAxis: dual,
      showLegend,
      legendPosition,
      rotatedXLabels: !!xAxisLabel.rotate,
    }),
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: categories,
      ...AXIS_X_STYLE,
      axisLabel: xAxisLabel,
    },
    yAxis,
    series,
  };
}

export default function LineChartWidget({ config, data, loading, error }: WidgetProps) {
  // Hook avant tout early return (règles des hooks) : le catalogue fournit les
  // libellés FR et le format d'affichage de chaque métrique.
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);
  const palette = resolveWidgetPalette(config.format?.palette as string | string[] | undefined, useChartPalette());

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const metrics = config.metrics ?? [];

  if (!data || !config.data_source_id || !metrics.length) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  const option = buildLineOption(config, data, catalog, palette);

  return (
    <div className={SHELL}>
      <ReactECharts option={option} notMerge style={{ height: '100%', minHeight: 200 }} />
    </div>
  );
}
